import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, map, mergeMap, of, switchMap, withLatestFrom } from "rxjs";
import { VersionClient } from "src/api-service";
import { selectSelectedCompany } from "src/app/store/user/user.selectors";
import { normalizeError } from "../../../app-store-error.types";
import { QuoteVersionActions } from "./version.actions";

@Injectable()
export class QuoteVersionEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly versionClient = inject(VersionClient);

    // =========================================================================
    // CORE VERSION OPERATIONS
    // =========================================================================

    createVersion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteVersionActions.createNewVersion),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ versionId, request }, selectedCompany]) =>
                this.versionClient.createVersion(versionId, request, selectedCompany.id).pipe(
                    mergeMap(result => [
                        QuoteVersionActions.createNewVersionSuccess({
                            version: result,
                            toastKey: 'QUOTE.VERSION.TOAST.CREATE_SUCCESS'
                        })
                    ]),
                    catchError(error => of(QuoteVersionActions.createNewVersionFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VERSION.TOAST.CREATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    updateVersion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteVersionActions.update),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([action, selectedCompany]) => {
                const { versionId, request } = action;
                const toastKey = action.toastKey ?? 'QUOTE.VERSION.TOAST.UPDATE_SUCCESS';
                return this.versionClient.patchVersion(versionId, request, selectedCompany.id).pipe(
                    mergeMap(version => [
                        QuoteVersionActions.updateVersionSuccess({ version, toastKey }),
                    ]),
                    catchError(error => of(QuoteVersionActions.updateVersionFailure({
                        error: normalizeError(error),
                        toastKey: toastKey.replace('SUCCESS', 'ERROR'),
                    }))),
                );
            }),
        ),
    );

    changeVersion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteVersionActions.changeVersion),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ version }, selectedCompany]) =>
                this.versionClient.touchVersion(version.id, selectedCompany.id).pipe(
                    mergeMap(result => [QuoteVersionActions.changeVersionSuccess({ version: result })]),
                    catchError(error => of(QuoteVersionActions.changeVersionFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VERSION.TOAST.CHANGE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // DISCOUNT (patchProjectDiscount)
    // =========================================================================

    updateDiscount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                QuoteVersionActions.updateDiscountAmount,
                QuoteVersionActions.updateDiscountPercent,
                QuoteVersionActions.updateDiscountDescription,
            ),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ versionId, request }, selectedCompany]) =>
                this.versionClient.patchProjectDiscount(versionId, request, selectedCompany.id).pipe(
                    mergeMap(result => [QuoteVersionActions.updateDiscountSuccess({
                        version: result,
                        toastKey: 'QUOTE.VERSION.TOAST.UPDATE_SUCCESS'
                    })]),
                    catchError(error => of(QuoteVersionActions.updateDiscountFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VERSION.TOAST.UPDATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // KICKBACK (patchProjectManagement)
    // =========================================================================

    updateKickback$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                QuoteVersionActions.updateKickbackDescription,
                QuoteVersionActions.updateKickbackPercent,
                QuoteVersionActions.updateKickbackAmount,
            ),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ versionId, request }, selectedCompany]) =>
                this.versionClient.patchProjectManagement(versionId, request, selectedCompany.id).pipe(
                    mergeMap(result => [QuoteVersionActions.updateKickbackSuccess({
                        version: result,
                        toastKey: 'QUOTE.VERSION.TOAST.UPDATE_SUCCESS'
                    })]),
                    catchError(error => of(QuoteVersionActions.updateKickbackFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VERSION.TOAST.UPDATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // PROJECT MANAGEMENT (patchProjectManagement)
    // =========================================================================

    updateProjectManagement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                QuoteVersionActions.updateManagementDescription,
                QuoteVersionActions.updateManagementHours,
                QuoteVersionActions.updateManagementAmount,
                QuoteVersionActions.updateManagementExclude,
                QuoteVersionActions.updateManagementAutoCalculation,
            ),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ versionId, request }, selectedCompany]) =>
                this.versionClient.patchProjectManagement(versionId, request, selectedCompany.id).pipe(
                    mergeMap(result => [QuoteVersionActions.updateManagementSuccess({
                        version: result,
                        toastKey: 'QUOTE.VERSION.TOAST.UPDATE_SUCCESS'
                    })]),
                    catchError(error => of(QuoteVersionActions.updateManagementFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VERSION.TOAST.UPDATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // SUMMARY (patchSummary)
    // =========================================================================

    updateSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteVersionActions.updateSummary),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ versionId, request }, selectedCompany]) =>
                this.versionClient.patchSummary(versionId, request, selectedCompany.id).pipe(
                    mergeMap(result => [QuoteVersionActions.updateSummarySuccess({
                        version: result,
                        toastKey: 'QUOTE.VERSION.TOAST.SUMMARY_SUCCESS'
                    })]),
                    catchError(error => of(QuoteVersionActions.updateSummaryFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VERSION.TOAST.UPDATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // BRAND INFORMATION (vendor endpoints)
    // =========================================================================

    updateVendor$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteVersionActions.updateVendor),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ versionId, request }, selectedCompany]) =>
                this.versionClient.updateVendors(selectedCompany.id, versionId, request).pipe(
                    mergeMap(result => [QuoteVersionActions.updateVendorSuccess({
                        version: result,
                        toastKey: 'QUOTE.VERSION.TOAST.VENDOR_SUCCESS'
                    })]),
                    catchError(error => of(QuoteVersionActions.updateVendorFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VERSION.TOAST.UPDATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    updateVendorSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteVersionActions.updateVendorSummary),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ versionId, vendorSummaryId, request }, selectedCompany]) =>
                this.versionClient.patchVendorSummary(versionId, vendorSummaryId, request, selectedCompany.id).pipe(
                    mergeMap(result => [QuoteVersionActions.updateVendorSummarySuccess({
                        version: result.version,
                        toastKey: 'QUOTE.VERSION.TOAST.VENDOR_SUMMARY_SUCCESS'
                    })]),
                    catchError(error => of(QuoteVersionActions.updateVendorSummaryFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VERSION.TOAST.UPDATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // QUOTE OPTIONS (patchSummary)
    // =========================================================================

    updateQuoteOptions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteVersionActions.updateQuoteOptions),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ versionId, request }, selectedCompany]) =>
                this.versionClient.patchSummary(versionId, request, selectedCompany.id).pipe(
                    mergeMap(result => [QuoteVersionActions.updateQuoteOptionsSuccess({
                        version: result,
                        toastKey: 'QUOTE.VERSION.TOAST.OPTIONS_SUCCESS'
                    })]),
                    catchError(error => of(QuoteVersionActions.updateQuoteOptionsFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VERSION.TOAST.UPDATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    patchIncludeCustomerPriceDecimals$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteVersionActions.patchIncludeCustomerPriceDecimals),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ versionId, request }, selectedCompany]) =>
                this.versionClient.patchSummary(versionId, request, selectedCompany.id).pipe(
                    mergeMap(result => [QuoteVersionActions.patchIncludeCustomerPriceDecimalsSuccess({
                        version: result,
                        toastKey: 'QUOTE.VERSION.TOAST.DECIMALS_SUCCESS'
                    })]),
                    catchError(error => of(QuoteVersionActions.patchIncludeCustomerPriceDecimalsFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VERSION.TOAST.UPDATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // =========================================================================
    // INTEREST RATE
    // =========================================================================

    refreshInterestRate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuoteVersionActions.refreshInterestRate),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ versionId }, selectedCompany]) =>
                this.versionClient.refreshInterestRate(selectedCompany.id, versionId).pipe(
                    mergeMap(result => [QuoteVersionActions.refreshInterestRateSuccess({
                        version: result,
                        toastKey: 'SUBSCRIPTION.TOAST.INTEREST_RATE_REFRESHED'
                    })]),
                    catchError(error => of(QuoteVersionActions.refreshInterestRateFailure({
                        error: normalizeError(error),
                        toastKey: 'SUBSCRIPTION.TOAST.INTEREST_RATE_REFRESH_ERROR'
                    }))),
                ),
            ),
        ),
    );
}
